import React, { Component } from 'react';
import { Card, Form, Button, Alert, Row, Col, Badge, Table, Spinner, Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import paymentService from '../../../../services/PaymentService';

class AddPayment extends Component {
    constructor(props) {
        super(props);

        this.state = {
            step: 1, // 1: Search card, 2: Payment form, 3: Confirmation

            // Card search
            searchQuery: '',
            searchType: 'cardNumber',
            searchResults: [],
            selectedCard: null,
            searchLoading: false,
            searchError: null,

            // Payment form
            paymentForm: {
                provider: 'OrangeMoney',
                phoneNumber: '',
                clientName: '',
                clientEmail: '',
                countryCode: 'SN',
                amount: '',
                currency: 'XOF',
                paymentReason: 'Production de carte',
                notes: ''
            },

            // Payment eligibility and cost
            eligibilityInfo: null,
            costCalculation: null,

            // Processing state
            processing: false,
            error: null
        };
    }

    // Step 1: Search card
    searchCard = async () => {
        const { searchType, searchQuery } = this.state;

        if (!searchQuery.trim()) {
            this.setState({ searchError: 'Veuillez saisir une valeur de recherche' });
            return;
        }

        this.setState({ searchLoading: true, searchError: null });

        try {
            const baseUrl = process.env.REACT_APP_BASEURL_CLIENT_BACKEND;
            let response;

            if (searchType === 'cardId' && !isNaN(searchQuery)) {
                response = await fetch(`${baseUrl}/Card/${searchQuery}`);
            } else if (searchType === 'cardNumber') {
                response = await fetch(`${baseUrl}/Card/number/${searchQuery}`);
            } else if (searchType === 'beneficiaryId') {
                response = await fetch(`${baseUrl}/Card/beneficiary/${searchQuery}`);
            }

            if (response.ok) {
                const data = await response.json();
                const cards = Array.isArray(data) ? data : [data];

                this.setState({
                    searchResults: cards,
                    searchError: cards.length === 0 ? 'Aucune carte trouvée' : null
                });
            } else if (response.status === 404) {
                this.setState({
                    searchResults: [],
                    searchError: 'Aucune carte trouvée avec ces critères'
                });
            } else {
                throw new Error('Erreur de recherche');
            }

        } catch (error) {
            console.error('Search error:', error);
            this.setState({ searchError: error.message || 'Erreur de recherche' });
        } finally {
            this.setState({ searchLoading: false });
        }
    };

    selectCard = async (card) => {
        this.setState({ selectedCard: card, processing: true, error: null });

        try {
            // Check payment eligibility
            const eligibility = await paymentService.checkCardPaymentEligibility(card.id);

            if (!eligibility.isEligible) {
                this.setState({
                    error: eligibility.reason,
                    eligibilityInfo: eligibility,
                    processing: false
                });
                return;
            }

            // Calculate cost
            const costCalculation = await paymentService.calculateCardCost(card.id, card.countryCode || 'SN');

            this.setState({
                eligibilityInfo: eligibility,
                costCalculation: costCalculation,
                paymentForm: {
                    ...this.state.paymentForm,
                    amount: costCalculation.totalCost,
                    currency: costCalculation.currency,
                    clientName: card.beneficiaryName || ''
                },
                step: 2
            });

        } catch (error) {
            console.error('Error checking eligibility:', error);
            this.setState({ error: error.message || 'Erreur lors de la vérification' });
        } finally {
            this.setState({ processing: false });
        }
    };

    // Step 2: Payment form
    handleFormChange = (field, value) => {
        this.setState({
            paymentForm: {
                ...this.state.paymentForm,
                [field]: value
            }
        });
    };

    validatePaymentForm = () => {
        const { paymentForm } = this.state;

        if (!paymentForm.clientName.trim()) {
            return 'Le nom du client est requis';
        }

        if (paymentForm.provider !== 'BankTransfer' && !paymentForm.phoneNumber.trim()) {
            return 'Le numéro de téléphone est requis';
        }

        if (!paymentForm.amount || paymentForm.amount <= 0) {
            return 'Le montant doit être supérieur à zéro';
        }

        return null;
    };

    proceedToConfirmation = () => {
        const error = this.validatePaymentForm();
        if (error) {
            this.setState({ error });
            return;
        }

        this.setState({ step: 3, error: null });
    };

    // Step 3: Process payment
    processPayment = async () => {
        const { selectedCard, paymentForm } = this.state;

        this.setState({ processing: true, error: null });

        try {
            const paymentData = {
                cardId: selectedCard.id,
                provider: paymentForm.provider,
                amount: parseFloat(paymentForm.amount),
                currency: paymentForm.currency,
                phoneNumber: paymentForm.phoneNumber,
                clientName: paymentForm.clientName,
                clientEmail: paymentForm.clientEmail,
                countryCode: paymentForm.countryCode,
                paymentReason: paymentForm.paymentReason,
                notes: paymentForm.notes
            };

            const result = await paymentService.processCardPaymentSimple(paymentData);

            if (result.success) {
                swal({
                    title: "Paiement réussi",
                    text: `Transaction ID: ${result.transactionId}`,
                    icon: "success",
                    button: "OK"
                }).then(() => {
                    this.resetForm();
                });
            } else {
                this.setState({ error: result.message });
            }

        } catch (error) {
            console.error('Payment error:', error);
            this.setState({ error: error.message || 'Erreur de paiement' });
        } finally {
            this.setState({ processing: false });
        }
    };

    resetForm = () => {
        this.setState({
            step: 1,
            searchQuery: '',
            searchResults: [],
            selectedCard: null,
            paymentForm: {
                provider: 'OrangeMoney',
                phoneNumber: '',
                clientName: '',
                clientEmail: '',
                countryCode: 'SN',
                amount: '',
                currency: 'XOF',
                paymentReason: 'Production de carte',
                notes: ''
            },
            eligibilityInfo: null,
            costCalculation: null,
            error: null
        });
    };

    // Render methods
    renderCardSearch = () => {
        const { searchType, searchQuery, searchResults, searchLoading, searchError } = this.state;

        return (
            <Card>
                <Card.Header>
                    <h6 className="mb-0">
                        <i className="fa fa-search me-2" />
                        Rechercher une carte
                    </h6>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Type de recherche</Form.Label>
                                <Form.Select
                                    value={searchType}
                                    onChange={(e) => this.setState({ searchType: e.target.value })}
                                >
                                    <option value="cardNumber">Par numéro de carte</option>
                                    <option value="cardId">Par ID de carte</option>
                                    <option value="beneficiaryId">Par ID bénéficiaire</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Valeur de recherche</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => this.setState({ searchQuery: e.target.value })}
                                    placeholder={
                                        searchType === 'cardNumber' ? 'Ex: AC2025123456789012' :
                                        searchType === 'cardId' ? 'Ex: 789' :
                                        'Ex: 12345'
                                    }
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Label>&nbsp;</Form.Label>
                            <div>
                                <Button
                                    variant="primary"
                                    onClick={this.searchCard}
                                    disabled={searchLoading}
                                    className="w-100"
                                >
                                    {searchLoading ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <i className="fa fa-search" />
                                    )}
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {searchError && (
                        <Alert variant="danger" className="mt-3">
                            <i className="fa fa-exclamation-triangle me-2" />
                            {searchError}
                        </Alert>
                    )}

                    {searchResults.length > 0 && (
                        <div className="mt-4">
                            <h6>Résultats de recherche:</h6>
                            <div className="table-responsive">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>Numéro de carte</th>
                                            <th>Bénéficiaire</th>
                                            <th>Statut</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchResults.map((card, index) => (
                                            <tr key={card.id || index}>
                                                <td>
                                                    <div className="fw-medium">{card.cardNumber}</div>
                                                    <small className="text-muted">ID: {card.id}</small>
                                                </td>
                                                <td>{card.beneficiaryName}</td>
                                                <td>
                                                    <Badge bg="info">{card.status}</Badge>
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={() => this.selectCard(card)}
                                                    >
                                                        <i className="fa fa-check me-1" />
                                                        Sélectionner
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    };

    renderPaymentForm = () => {
        const { paymentForm, selectedCard, costCalculation, processing } = this.state;

        return (
            <Row>
                <Col md={6}>
                    <Card className="mb-4">
                        <Card.Header>
                            <h6 className="mb-0">
                                <i className="fa fa-id-card me-2" />
                                Carte sélectionnée
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <p><strong>Numéro:</strong> {selectedCard.cardNumber}</p>
                            <p><strong>Bénéficiaire:</strong> {selectedCard.beneficiaryName}</p>
                            <p><strong>Statut:</strong> <Badge bg="info">{selectedCard.status}</Badge></p>
                            {costCalculation && (
                                <>
                                    <hr />
                                    <p><strong>Coût total:</strong> <span className="h6 text-primary">{costCalculation.totalCost.toLocaleString()} {costCalculation.currency}</span></p>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <h6 className="mb-0">
                                <i className="fa fa-credit-card me-2" />
                                Informations de paiement
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Fournisseur de paiement</Form.Label>
                                            <Form.Select
                                                value={paymentForm.provider}
                                                onChange={(e) => this.handleFormChange('provider', e.target.value)}
                                            >
                                                <option value="OrangeMoney">Orange Money</option>
                                                <option value="MtnMoney">MTN Money</option>
                                                <option value="BankTransfer">Virement Bancaire</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Numéro de téléphone</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                value={paymentForm.phoneNumber}
                                                onChange={(e) => this.handleFormChange('phoneNumber', e.target.value)}
                                                placeholder="+221 77 123 45 67"
                                                disabled={paymentForm.provider === 'BankTransfer'}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom du client</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={paymentForm.clientName}
                                                onChange={(e) => this.handleFormChange('clientName', e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email (optionnel)</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={paymentForm.clientEmail}
                                                onChange={(e) => this.handleFormChange('clientEmail', e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Montant</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={paymentForm.amount}
                                                onChange={(e) => this.handleFormChange('amount', e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Devise</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={paymentForm.currency}
                                                onChange={(e) => this.handleFormChange('currency', e.target.value)}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Motif du paiement</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={paymentForm.paymentReason}
                                        onChange={(e) => this.handleFormChange('paymentReason', e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Notes (optionnel)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={paymentForm.notes}
                                        onChange={(e) => this.handleFormChange('notes', e.target.value)}
                                        placeholder="Notes additionnelles sur le paiement"
                                    />
                                </Form.Group>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    };

    renderConfirmation = () => {
        const { selectedCard, paymentForm } = this.state;

        return (
            <Card>
                <Card.Header>
                    <h6 className="mb-0">
                        <i className="fa fa-check-circle me-2" />
                        Confirmation du paiement
                    </h6>
                </Card.Header>
                <Card.Body>
                    <Alert variant="info">
                        <i className="fa fa-info-circle me-2" />
                        Veuillez vérifier les informations avant de confirmer le paiement.
                    </Alert>

                    <Row>
                        <Col md={6}>
                            <h6>Informations de la carte</h6>
                            <Table borderless size="sm">
                                <tbody>
                                    <tr>
                                        <td><strong>Numéro:</strong></td>
                                        <td>{selectedCard.cardNumber}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Bénéficiaire:</strong></td>
                                        <td>{selectedCard.beneficiaryName}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Statut:</strong></td>
                                        <td><Badge bg="info">{selectedCard.status}</Badge></td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Col>

                        <Col md={6}>
                            <h6>Détails du paiement</h6>
                            <Table borderless size="sm">
                                <tbody>
                                    <tr>
                                        <td><strong>Fournisseur:</strong></td>
                                        <td>{paymentForm.provider}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Montant:</strong></td>
                                        <td><strong className="text-primary">{parseFloat(paymentForm.amount).toLocaleString()} {paymentForm.currency}</strong></td>
                                    </tr>
                                    <tr>
                                        <td><strong>Client:</strong></td>
                                        <td>{paymentForm.clientName}</td>
                                    </tr>
                                    {paymentForm.phoneNumber && (
                                        <tr>
                                            <td><strong>Téléphone:</strong></td>
                                            <td>{paymentForm.phoneNumber}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    render() {
        const { step, processing, error } = this.state;

        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h5 className="mb-1">
                            <i className="fa fa-plus-circle me-2" />
                            Ajouter un paiement
                        </h5>
                        <p className="text-muted mb-0">Traiter un paiement pour une carte</p>
                    </div>

                    {step > 1 && (
                        <Button variant="outline-secondary" onClick={this.resetForm}>
                            <i className="fa fa-arrow-left me-2" />
                            Recommencer
                        </Button>
                    )}
                </div>

                {/* Progress indicator */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between">
                        <div className={`text-center ${step >= 1 ? 'text-primary' : 'text-muted'}`}>
                            <div className={`rounded-circle border d-inline-flex align-items-center justify-content-center ${step >= 1 ? 'border-primary bg-primary text-white' : 'border-muted'}`} style={{width: '40px', height: '40px'}}>
                                {step > 1 ? <i className="fa fa-check" /> : '1'}
                            </div>
                            <div className="mt-1 small">Rechercher carte</div>
                        </div>
                        <div className={`text-center ${step >= 2 ? 'text-primary' : 'text-muted'}`}>
                            <div className={`rounded-circle border d-inline-flex align-items-center justify-content-center ${step >= 2 ? 'border-primary bg-primary text-white' : 'border-muted'}`} style={{width: '40px', height: '40px'}}>
                                {step > 2 ? <i className="fa fa-check" /> : '2'}
                            </div>
                            <div className="mt-1 small">Informations paiement</div>
                        </div>
                        <div className={`text-center ${step >= 3 ? 'text-primary' : 'text-muted'}`}>
                            <div className={`rounded-circle border d-inline-flex align-items-center justify-content-center ${step >= 3 ? 'border-primary bg-primary text-white' : 'border-muted'}`} style={{width: '40px', height: '40px'}}>
                                3
                            </div>
                            <div className="mt-1 small">Confirmation</div>
                        </div>
                    </div>
                    <hr className="my-3" />
                </div>

                {error && (
                    <Alert variant="danger" className="mb-4">
                        <i className="fa fa-exclamation-triangle me-2" />
                        {error}
                    </Alert>
                )}

                {processing && (
                    <Alert variant="info" className="mb-4">
                        <div className="d-flex align-items-center">
                            <Spinner size="sm" className="me-2" />
                            Traitement en cours...
                        </div>
                    </Alert>
                )}

                {/* Step content */}
                {step === 1 && this.renderCardSearch()}
                {step === 2 && this.renderPaymentForm()}
                {step === 3 && this.renderConfirmation()}

                {/* Step navigation */}
                {step === 2 && (
                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="primary" onClick={this.proceedToConfirmation}>
                            <i className="fa fa-arrow-right me-2" />
                            Continuer vers la confirmation
                        </Button>
                    </div>
                )}

                {step === 3 && (
                    <div className="d-flex justify-content-end mt-4 gap-2">
                        <Button variant="outline-secondary" onClick={() => this.setState({ step: 2 })}>
                            <i className="fa fa-arrow-left me-2" />
                            Retour
                        </Button>
                        <Button
                            variant="success"
                            onClick={this.processPayment}
                            disabled={processing}
                        >
                            {processing ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Traitement...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-check me-2" />
                                    Confirmer le paiement
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

export default AddPayment;